import { useCallback, useEffect, useState, useMemo } from "react";
import { DateRange } from "react-day-picker";
import { format, differenceInDays, addDays } from "date-fns";
import { toast } from "sonner";
import {
  Play,
  Pause,
  Loader2,
  Map,
  Calendar,
  Clock,
  Activity,
  Plane,
  PlayCircle,
  PauseCircle,
  RotateCcw as ResetIcon,
  Info
} from "lucide-react";
import { Map as MapComponent, Source, Layer } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import React from "react";

import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyMedia } from "@/components/ui/empty";
import { ProOnlyGuard } from "@/components/pro-only-guard";
import { useStore } from "@/store/store";
import { useDetectionReplayQuery } from "@/api/v0/detections";
import { Detection } from "@/store/detection-slice";
import MovementEvents from "@/components/map/movement-events";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";

export const DetectionReplayRoute = () => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentTime, setCurrentTime] = useState<Date | null>(null);

  // Get map state from store
  const mapBounds = useStore(useCallback((state) => state.mapBounds, []));
  const mapZoom = useStore(useCallback((state) => state.mapZoom, []));
  const mapStyleUrl = useStore(useCallback((state) => state.mapStyleUrl, []));

  // Initialize map bounds if not set
  useEffect(() => {
    if (!mapBounds) {
      useStore.getState().setMapBounds([-0.1276, 51.5074]); // Default to London
      useStore.getState().setMapZoom(10);
    }
  }, [mapBounds]);

  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = useDetectionReplayQuery(dateRange);

  // Combine all detections from paginated results
  const allDetections = useMemo(() => {
    return data?.pages.flatMap(page => page.detections) ?? [];
  }, [data]);

  // Get first and last detection times, ensuring proper order and filtering out 0,0 positions
  const sortedDetections = useMemo(() => {
    return [...allDetections]
      .filter(d => !(d.latitude === 0 && d.longitude === 0)) // Filter out 0,0 positions
      .sort((a, b) => 
        new Date(a.detection_time).getTime() - new Date(b.detection_time).getTime()
      );
  }, [allDetections]);

  const firstDetection = sortedDetections.length > 0 
    ? new Date(sortedDetections[0].detection_time)
    : null;
  const lastDetection = sortedDetections.length > 0
    ? new Date(sortedDetections[sortedDetections.length - 1].detection_time)
    : null;

  

  // Update current time based on progress
  useEffect(() => {
    if (!dateRange?.from || !dateRange?.to) return;

    const startTime = dateRange.from.getTime();
    const endTime = dateRange.to.getTime();
    const totalDuration = endTime - startTime;
    const currentTimeMs = startTime + (totalDuration * (progress / 100));
    
    setCurrentTime(new Date(currentTimeMs));
  }, [progress, dateRange]);

  
  // Get all detections that have occurred and are within 30 seconds of current time
  const detectionsUpToNow = useMemo(() => {
    if (!currentTime) return [];
    const currentTimeMs = currentTime.getTime();
    const thirtySecondsMs = 30 * 1000;
    
    return sortedDetections.filter(d => {
      const detectionTime = new Date(d.detection_time).getTime();
      // Only include detections that:
      // 1. Have already occurred (detection time <= current time)
      // 2. Are within the last 30 seconds
      return detectionTime <= currentTimeMs && 
             detectionTime >= (currentTimeMs - thirtySecondsMs);
    });
  }, [currentTime, sortedDetections]);

  // Group detections by drone identifier and create track segments
  const droneDetections = useMemo(() => {
    const groups: Record<string, Detection[]> = {};
    
    detectionsUpToNow.forEach((detection: Detection) => {
      const droneId = detection.drone_identifier;
      if (!groups[droneId]) {
        groups[droneId] = [];
      }
      groups[droneId].push(detection);
    });

    return Object.entries(groups).map(([droneId, detections]) => {
      const sortedDroneDetections = detections.sort((a: Detection, b: Detection) => 
        new Date(b.detection_time).getTime() - new Date(a.detection_time).getTime()
      );

      // Split track into segments where gaps are > 15 seconds
      const trackSegments: Detection[][] = [];
      let currentSegment: Detection[] = [];
      const fifteenSecondsMs = 15 * 1000;

      // Start with the most recent detection and work backwards
      sortedDroneDetections.forEach((detection: Detection, index: number) => {
        if (index === 0) {
          currentSegment.push(detection);
        } else {
          const currentTime = new Date(detection.detection_time).getTime();
          const prevTime = new Date(sortedDroneDetections[index - 1].detection_time).getTime();
          
          if (Math.abs(currentTime - prevTime) <= fifteenSecondsMs) {
            currentSegment.push(detection);
          } else {
            if (currentSegment.length > 0) {
              trackSegments.push([...currentSegment]);
            }
            currentSegment = [detection];
          }
        }
      });
      
      if (currentSegment.length > 0) {
        trackSegments.push(currentSegment);
      }

      return {
        droneId,
        mostRecent: sortedDroneDetections[0],
        trackSegments
      };
    });
  }, [detectionsUpToNow]);

  // Playback control
  useEffect(() => {
    if (!isPlaying || !dateRange?.from || !dateRange?.to) return;

    const startTime = dateRange.from.getTime();
    const endTime = dateRange.to.getTime();
    const totalDurationMs = endTime - startTime;
    
    // Calculate progress increment based on real-time playback speed
    const progressPerSecond = (1000 / totalDurationMs) * 100;
    const progressPerTick = (progressPerSecond / 100) * playbackSpeed;

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setIsPlaying(false);
          return 100;
        }
        return Math.min(100, prev + progressPerTick);
      });
    }, 10);

    return () => clearInterval(interval);
  }, [isPlaying, dateRange, playbackSpeed]);

  // Load more data if we're getting close to the end
  useEffect(() => {
    if (hasNextPage && !isFetching && progress > 80) {
      fetchNextPage();
    }
  }, [progress, hasNextPage, isFetching, fetchNextPage]);

  // Calculate detection markers for the slider
  const detectionMarkers = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return [];
    
    const startTime = dateRange.from.getTime();
    const endTime = dateRange.to.getTime();
    const totalDuration = endTime - startTime;

    return sortedDetections.map(detection => {
      const detectionTime = new Date(detection.detection_time).getTime();
      return ((detectionTime - startTime) / totalDuration) * 100;
    });
  }, [dateRange, sortedDetections]);

  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (!range?.from || !range?.to) {
      setDateRange(range);
      return;
    }

    // Ensure from date is before to date
    if (range.from > range.to) {
      toast.error("Start date must be before end date");
      return;
    }

    const daysDifference = differenceInDays(range.to, range.from);
    if (daysDifference > 7) {
      toast.error("Date range cannot exceed 7 days for replay");
      setDateRange({
        from: range.from,
        to: addDays(range.from, 7)
      });
      return;
    }

    setDateRange(range);
    setProgress(0);
    setIsPlaying(false);
  };

  return (
    <ProOnlyGuard
      title="Detection Replay"
      description="Replay and analyze historical drone detection data. Available for Pro subscribers."
    >
      <main>
        <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        <BreadcrumbNav
          items={[
            { label: "Home", href: "/" },
            { label: "Detection Replay", href: "/detection-replay" },
          ]}
          className="mb-6"
        />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold">Detection Replay</h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Replay historical drone detections with interactive timeline controls
            </p>
          </div>
          <Badge variant="outline" className="w-fit">
            <PlayCircle className="h-3 w-3 mr-1" />
            Time-based Analysis
          </Badge>
        </div>

        {/* Controls Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Replay Controls
            </CardTitle>
            <CardDescription>
              {firstDetection && lastDetection ? (
                `Detection range: ${format(firstDetection, 'PPpp')} to ${format(lastDetection, 'PPpp')}`
              ) : (
                "Select a date range to begin replay analysis (maximum 7 days)"
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Date Range Selection */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
              <div className="flex-1">
                <DateRangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  disabled={isLoading || isPlaying}
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  size="icon"
                  disabled={isLoading || !allDetections.length}
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="h-10 w-10"
                >
                  {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  disabled={isLoading || !allDetections.length}
                  onClick={() => {
                    setProgress(0);
                    setIsPlaying(false);
                  }}
                  className="h-10 w-10"
                >
                  <ResetIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Timeline Controls */}
            {allDetections.length > 0 && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Timeline</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>{currentTime ? format(currentTime, 'PPpp') : '--'}</span>
                    <Badge variant="outline">{Math.round(progress)}%</Badge>
                  </div>
                </div>
                
                {/* Detection markers and slider */}
                <div className="relative pt-2">
                  {/* Detection markers */}
                  <div className="absolute w-full h-1 -top-1">
                    {detectionMarkers.map((position, index) => (
                      <div
                        key={index}
                        className="absolute w-0.5 h-1 bg-primary"
                        style={{
                          left: `${position}%`,
                          transform: 'translateX(-50%)'
                        }}
                      />
                    ))}
                  </div>
                  
                  {/* Slider */}
                  <Slider
                    value={[progress]}
                    onValueChange={([value]) => {
                      setProgress(value);
                      setIsPlaying(false);
                    }}
                    max={100}
                    step={0.1}
                    disabled={isLoading || !allDetections.length}
                    className="w-full"
                  />
                </div>

                {/* Playback Speed Controls */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                  <span className="text-sm text-muted-foreground">Playback Speed (real-time multiplier)</span>
                  <div className="flex gap-2 flex-wrap">
                    {[1, 60, 300, 3600, 86400].map(speed => (
                      <Button
                        key={speed}
                        variant={playbackSpeed === speed ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPlaybackSpeed(speed)}
                        disabled={isLoading || !allDetections.length}
                        className="h-8"
                      >
                        {speed === 1 ? '1x' :
                         speed === 60 ? '1m' :
                         speed === 300 ? '5m' :
                         speed === 3600 ? '1h' :
                         '1d'}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Map Container */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Map className="h-5 w-5" />
              Detection Replay Map
            </CardTitle>
            <CardDescription>
              {allDetections.length > 0 
                ? `Replaying ${allDetections.length} detections across ${Object.keys(droneDetections).length} drones`
                : "No detection data available for the selected date range"
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="relative h-[600px] w-full">
              <MapComponent
                id="map"
                initialViewState={{
                  longitude: mapBounds?.[0] ?? -0.1276,
                  latitude: mapBounds?.[1] ?? 51.5074,
                  zoom: mapZoom ?? 10,
                }}
                style={{ width: "100%", height: "100%" }}
                mapStyle={mapStyleUrl}
                maxZoom={16}
                minZoom={2}
                maxPitch={0}
              >
                <MovementEvents />
                
                {droneDetections.map(({ droneId, mostRecent, trackSegments }: { droneId: string; mostRecent: Detection; trackSegments: Detection[][] }) => (
                  <React.Fragment key={droneId}>
                    {/* Most recent detection with drone icon */}
                    <Source
                      type="geojson"
                      data={{
                        type: "Feature",
                        geometry: {
                          type: "Point",
                          coordinates: [mostRecent.longitude, mostRecent.latitude]
                        },
                        properties: {
                          droneId: mostRecent.drone_identifier,
                          sensorId: mostRecent.sensor_id,
                          ...(typeof mostRecent.drone_details === 'string' ? JSON.parse(mostRecent.drone_details) : mostRecent.drone_details)
                        }
                      }}
                    >
                      <Layer
                        type="symbol"
                        layout={{
                          "icon-image": "drone",
                          "icon-size": 0.5,
                          "icon-allow-overlap": true
                        }}
                      />
                    </Source>

                    {/* Track points for all segments */}
                    {trackSegments.map((segment: Detection[], segmentIndex: number) => (
                      <React.Fragment key={`${droneId}-segment-${segmentIndex}`}>
                        {/* Track points */}
                        <Source
                          type="geojson"
                          data={{
                            type: "FeatureCollection",
                            features: segment.slice(1).map((detection: Detection) => ({
                              type: "Feature",
                              geometry: {
                                type: "Point",
                                coordinates: [detection.longitude, detection.latitude]
                              },
                              properties: {
                                droneId: detection.drone_identifier,
                                sensorId: detection.sensor_id
                              }
                            }))
                          }}
                        >
                          <Layer
                            type="circle"
                            paint={{
                              "circle-radius": 4,
                              "circle-color": "#ff4444",
                              "circle-opacity": 0.6,
                            }}
                          />
                        </Source>

                        {/* Track line for this segment */}
                        <Source
                          type="geojson"
                          data={{
                            type: "Feature",
                            geometry: {
                              type: "LineString",
                              coordinates: segment.map((d: Detection) => [d.longitude, d.latitude])
                            },
                            properties: {
                              droneId
                            }
                          }}
                        >
                          <Layer
                            type="line"
                            paint={{
                              "line-color": "#ff4444",
                              "line-width": 2,
                              "line-opacity": 0.4,
                            }}
                          />
                        </Source>
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </MapComponent>

              {/* Loading Overlay */}
              {(isLoading || isFetching) && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-sm">
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">Loading detection data...</p>
                  </div>
                </div>
              )}

              {/* No Data State */}
              {!isLoading && !isFetching && allDetections.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Empty>
                    <EmptyMedia variant="icon">
                      <Activity className="h-6 w-6" />
                    </EmptyMedia>
                    <EmptyHeader>
                      <EmptyTitle>No Detection Data</EmptyTitle>
                      <EmptyDescription>
                        No drone detections found for the selected date range. Try adjusting your date range or check if sensors are active.
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Statistics Cards */}
        {allDetections.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Detections</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{allDetections.length}</div>
                <p className="text-xs text-muted-foreground">
                  Detection events
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Unique Drones</CardTitle>
                <Plane className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{Object.keys(droneDetections).length}</div>
                <p className="text-xs text-muted-foreground">
                  Different aircraft
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Time Range</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {firstDetection && lastDetection 
                    ? `${differenceInDays(lastDetection, firstDetection) + 1}d`
                    : '--'
                  }
                </div>
                <p className="text-xs text-muted-foreground">
                  Analysis period
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Status</CardTitle>
                <PlayCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isPlaying ? (
                  <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    <PauseCircle className="h-3 w-3 mr-1" />
                    Playing
                  </Badge>
                ) : (
                  <Badge variant="outline">
                    <PlayCircle className="h-3 w-3 mr-1" />
                    Paused
                  </Badge>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Replay status
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Help Information */}
        <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
          <Info className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-semibold">Using Detection Replay</p>
              <p>
                Use the timeline slider to scrub through time, or click play to watch detections unfold automatically. 
                Adjust playback speed to analyze patterns quickly or in detail. Red dots show detection points, 
                and lines connect sequential detections from the same drone.
              </p>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    </main>
    </ProOnlyGuard>
  );
}; 
